"use client";
import { useCallback, useEffect, useRef } from "react";
import { generateUuid } from "@/lib/utils/util";
import { useSessionStore } from "@/lib/store/session";
import { usePathname } from "next/navigation";
// 直接导入 animate.css 避免 Cloudflare CDN 第三方 Cookie

declare global {
	interface Window {
		Trustpilot?: {
			loadFromElement: (element: Element | null, refresh: boolean) => void;
		};
	}
}

/**
 * 设置 Trustpilot 脚本请求拦截器
 * 将 widget.trustpilot.com 的脚本请求重定向到本地代理
 * 优化缓存生命周期从1天到30天，节省约13KiB带宽
 */
const setupTrustpilotProxyInterceptor = () => {
	// 检查是否已设置拦截器
	if ((window as unknown as { __tpProxySetup?: boolean }).__tpProxySetup) return;
	(window as unknown as { __tpProxySetup?: boolean }).__tpProxySetup = true;

	// 保存原始 fetch 函数
	const originalFetch = window.fetch.bind(window);

	// 重写 fetch 函数以拦截 Trustpilot 请求
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(window as any).fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
		const url =
			typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url;

		// 检查是否是 Trustpilot 的脚本请求
		if (url.includes("widget.trustpilot.com/trustboxes/") && url.endsWith("/main.js")) {
			try {
				const urlObj = new URL(url);
				// 提取路径部分，例如 "trustboxes/xxx/main.js"
				const path = urlObj.pathname.replace(/^\//, "");
				// 重定向到本地代理
				const proxyUrl = `/api/proxy/trustpilot?path=${encodeURIComponent(path)}`;
				return originalFetch(proxyUrl, init);
			} catch {
				// URL 解析失败，使用原始请求
				return originalFetch(input, init);
			}
		}

		return originalFetch(input, init);
	};

	// 拦截动态创建的 script 标签
	const originalCreateElement = document.createElement.bind(document);
	document.createElement = function <K extends keyof HTMLElementTagNameMap>(
		tagName: K,
		options?: ElementCreationOptions,
	): HTMLElementTagNameMap[K] {
		const element = originalCreateElement(tagName, options);

		if (tagName.toLowerCase() === "script") {
			const scriptElement = element as HTMLScriptElement;
			// 使用 Object.defineProperty 拦截 src 属性设置
			let originalSrc = "";
			Object.defineProperty(scriptElement, "src", {
				get() {
					return originalSrc;
				},
				set(value: unknown) {
					// 确保 value 是字符串类型
					const strValue = typeof value === "string" ? value : String(value || "");
					if (
						strValue &&
						strValue.includes("widget.trustpilot.com/trustboxes/") &&
						strValue.endsWith("/main.js")
					) {
						try {
							const urlObj = new URL(strValue);
							const path = urlObj.pathname.replace(/^\//, "");
							originalSrc = `/api/proxy/trustpilot?path=${encodeURIComponent(path)}`;
						} catch {
							originalSrc = strValue;
						}
					} else {
						originalSrc = strValue;
					}
					scriptElement.setAttribute("src", originalSrc);
				},
				configurable: true,
			});
		}

		return element;
	};
};

/**
 * 客户端组件
 *
 * 该组件用于：
 * 1. 维护会话UUID
 * 2. 确保 Trustpilot 在每次路由切换时正确渲染
 * 3. 延迟加载非关键CSS以优化LCP
 */
export default function Client() {
	const { session_uuid, setSessionUuid } = useSessionStore();
	const pathname = usePathname();
	const refreshTimerRef = useRef<number | null>(null);

	// 设置 Trustpilot 代理拦截器（需要尽早执行）
	useEffect(() => {
		setupTrustpilotProxyInterceptor();
	}, []);

	// 确保 session_uuid 始终有效
	useEffect(() => {
		const session = JSON.parse(localStorage.getItem("session-store") || "{}");
		// @ts-ignore
		if (!session?.state?.session_uuid) {
			setSessionUuid(generateUuid());
		}
	}, [session_uuid]);

	/**
	 * 重新渲染 Trustpilot 小部件
	 */
	const refreshTrustpilotWidgets = useCallback((retryCount = 0) => {
		if (refreshTimerRef.current) {
			window.clearTimeout(refreshTimerRef.current);
		}

		refreshTimerRef.current = window.setTimeout(() => {
			const widgets = document.querySelectorAll(".trustpilot-widget");

			if (window.Trustpilot && widgets.length > 0) {
				widgets.forEach((widget) => {
					window.Trustpilot?.loadFromElement(widget, true);
				});
				return;
			}

			// Trustpilot 脚本使用 lazyOnload，首次进入或客户端路由切换时可能晚于组件挂载。
			if (retryCount < 20) {
				refreshTrustpilotWidgets(retryCount + 1);
			}
		}, 300);
	}, []);

	// 首次加载以及客户端路由变化后，重新渲染 Trustpilot 小部件
	useEffect(() => {
		refreshTrustpilotWidgets();

		return () => {
			if (refreshTimerRef.current) {
				window.clearTimeout(refreshTimerRef.current);
			}
		};
	}, [pathname, refreshTrustpilotWidgets]);

	return null;
}
